import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useAuth } from './hooks/useAuth';
import { trackPageView, setUserId } from './utils/analytics';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MainLayout from './layouts/MainLayout';
import RootLayout from './layouts/RootLayout';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import FAQ from './pages/FAQ';
import ContentPage from './pages/ContentPage';

function AnalyticsTracker() {
  const location = useLocation();
  const { session } = useAuth();

  useEffect(() => {
    trackPageView(document.title, location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    if (session?.user?.id) {
      setUserId(session.user.id);
    }
  }, [session]);

  return null;
}

function App() {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Router>
        <AnalyticsTracker />
        <Routes>
          <Route element={<RootLayout />}>
            {/* Public routes */}
            <Route index element={<MainLayout />} />
            <Route path="/login" element={!session ? <Login /> : <Navigate to="/" replace />} />
            <Route path="/register" element={!session ? <Register /> : <Navigate to="/" replace />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={!session ? <ForgotPassword /> : <Navigate to="/" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />

            {/* Dynamic content routes */}
            <Route path="/facts/:slug" element={<ContentPage type="fact" />} />
            <Route path="/animal-facts/:slug" element={<ContentPage type="animal" />} />
            <Route path="/science/:slug" element={<ContentPage type="science" />} />
            <Route path="/records/:slug" element={<ContentPage type="record" />} />
            <Route path="/inventions/:slug" element={<ContentPage type="invention" />} />
            <Route path="/numbers/:slug" element={<ContentPage type="number" />} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={session ? <MainLayout /> : <Navigate to="/login" replace />}
            />
          </Route>
        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;